import React, { useEffect, useState } from 'react';
import { Box, Typography, Breadcrumbs, Divider, Card, Grid, Skeleton, Chip, IconButton } from '@mui/material';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { InfoOutlined } from '@mui/icons-material';
import { getGlobalConfigByIdStore } from 'store/services-ia-global-config';
import { getApplicationDataDropDownStore } from 'store/app-releases';
import { InfoModal } from 'components/generics/InfoModal';

export const DetailAjustesGlobalesIa = () => {
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    
    const [loading, setLoading] = useState(true);
    const { global_config, applicationsListSelect } = useSelector((state: any) => state.servicesIaGlobalConfig);

    useEffect(() => {
        dispatch(getApplicationDataDropDownStore());
    }, [dispatch]);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        if (!id) return;
        setLoading(true);
        const response = await dispatch(getGlobalConfigByIdStore(id));
        if (!response?.ok) {
            navigate('/manager/plataforma-ia/configuracion/ajustes');
        }
        setLoading(false);
    };

    const getServiceName = (serviceId: string) => {
        if (!applicationsListSelect) return serviceId;
        const found = applicationsListSelect.find((app: any) => app.value === serviceId);
        return found ? found.label : serviceId;
    };

    const [activeModal, setActiveModal] = useState<string | null>(null);

    const getModalContent = () => {
        switch (activeModal) {
            case 'defaultChatServiceId': return { title: 'Servicio Chat', description: 'Servicio de IA por defecto utilizado para tareas conversacionales y generación de texto.', infoText: 'Ejemplo: Si eliges Gemini 1.5 Pro o GPT-4, todas las respuestas de texto de la aplicación usarán este modelo a menos que se especifique otro.' };
            case 'defaultImageServiceId': return { title: 'Servicio Imagen', description: 'Servicio de IA por defecto para la generación, análisis y edición de imágenes.', infoText: 'Ejemplo: Podrías seleccionar DALL-E 3 para generar avatares y miniaturas automáticamente.' };
            case 'defaultAudioServiceId': return { title: 'Servicio Audio', description: 'Servicio de IA por defecto para procesamiento de audio (transcripción y generación de voz).', infoText: 'Ejemplo: Whisper de OpenAI para convertir mensajes de voz de usuarios en texto.' };
            case 'defaultEmbeddingServiceId': return { title: 'Servicio Embedding', description: 'Servicio por defecto para la vectorización de datos, esencial para búsquedas semánticas y bases de conocimiento.', infoText: 'Ejemplo: Modelos como text-embedding-3-small se usan para comparar qué tan similares son dos documentos de políticas del hotel.' };
            case 'defaultOcrServiceId': return { title: 'Servicio OCR', description: 'Servicio por defecto para extraer texto estructurado de documentos e imágenes (Reconocimiento Óptico de Caracteres).', infoText: 'Ejemplo: Al subir la foto de una factura, se usa OCR para extraer automáticamente el número de comprobante y el total.' };
            case 'creditsPerUsd': return { title: 'Créditos por USD', description: 'Tasa de conversión que define cuántos créditos internos obtiene el usuario por cada dólar.', infoText: 'Ejemplo: Si configuras 100, un recargo de $5 USD agregará 500 créditos al balance del cliente.' };
            case 'defaultTemperature': return { title: 'Temperatura Base', description: 'Controla el nivel de creatividad de la respuesta. Un valor más cercano a 0 es más determinista y conservador, mientras que hacia 2 es más aleatorio.', infoText: 'Ejemplo: Usa 0.2 para respuestas técnicas exactas (como políticas de devolución) y 0.8 para redacción creativa (como sugerir ideas de marketing).' };
            case 'defaultTopP': return { title: 'Top-P Base', description: 'Otra forma de controlar la aleatoriedad, conocida como muestreo de núcleo. Limita el modelo a elegir entre el top % de palabras probables.', infoText: 'Ejemplo: 0.9 significa que el modelo solo considerará el 90% de las opciones más probables, ignorando palabras muy inusuales.' };
            case 'defaultMaxTokens': return { title: 'Tokens Máximos Base', description: 'Límite máximo por defecto de la cantidad de tokens (palabras o fragmentos) que el modelo puede devolver.', infoText: 'Ejemplo: Si pones 500, garantizas que las respuestas nunca sean excesivamente largas, protegiendo así tus costos de API.' };
            case 'enableStreaming': return { title: 'Streaming', description: 'Habilita la respuesta en tiempo real ("efecto máquina de escribir") en toda la aplicación.', infoText: 'Ejemplo: En lugar de esperar 5 segundos viendo "Cargando...", el usuario verá aparecer el texto palabra por palabra inmediatamente.' };
            case 'enableMemory': return { title: 'Memoria Compartida', description: 'Permite que el sistema recuerde contextos de conversaciones anteriores para mejorar la continuidad.', infoText: 'Ejemplo: Si un usuario preguntó por los restaurantes del hotel ayer, la IA recordará esto si pregunta "cuál era el de mariscos".' };
            case 'enableCache': return { title: 'Caché Global', description: 'Almacena respuestas a preguntas frecuentes para ahorrar costos y reducir la latencia.', infoText: 'Ejemplo: Si 10 personas preguntan "¿A qué hora es el check-in?", la primera petición va a OpenAI, pero las otras 9 se responden desde la caché de forma gratuita e instantánea.' };
            case 'enableTools': return { title: 'Llamadas a Funciones', description: 'Permite que los agentes de IA ejecuten funciones internas de la plataforma.', infoText: 'Ejemplo: El bot podría conectarse al sistema de reservaciones para ejecutar la función `buscarHabitacion(fecha)` en lugar de solo dar texto.' };
            case 'enableLogs': return { title: 'Registro Completo', description: 'Almacena detalladamente todos los prompts y completions para auditoría.', infoText: 'Ejemplo: Podrás revisar exactamente qué le preguntó el huésped a la IA y qué le respondió si surge una queja en recepción.' };
            case 'allowBringYourOwnKey': return { title: 'Llevar su Propia Clave (BYOK)', description: 'Permite a los usuarios avanzados utilizar su propia API Key (ej. de OpenAI) en lugar de consumir los créditos del sistema.', infoText: 'Ejemplo: Empresas con contratos propios de OpenAI ingresan su llave y tú ya no asumes sus costos operativos de IA.' };
            default: return { title: '', description: '', infoText: '' };
        }
    };
    
    const modalContent = getModalContent();

    const renderLabelWithInfo = (label: string, modalKey: string, isChipLabel = false) => (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: isChipLabel ? 1 : 0 }}>
            <Typography variant="body2" color="textSecondary">{label}</Typography>
            <IconButton size="small" color="info" onClick={() => setActiveModal(modalKey)} sx={{ ml: 0.5, p: 0.5 }}>
                <InfoOutlined fontSize="small" />
            </IconButton>
        </Box>
    );

    if (loading) return <Box sx={{ p: 4 }}><Skeleton variant="rectangular" height={400} /></Box>;

    const item = global_config || {};

    return (
        <Box sx={{ width: '100%' }}>
            <Typography fontSize={25} color="textPrimary" sx={{ fontWeight: 700 }}>Detalle Configuración Global</Typography>
            <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
                <Typography color="textPrimary" sx={{ fontWeight: 700 }}>Manager</Typography>
                <Link color="inherit" to="/manager/plataforma-ia/configuracion/ajustes">
                    Configuración Global
                </Link>
                <Typography color="textPrimary">Detalle</Typography>
            </Breadcrumbs>
            
            <Divider sx={{ marginBottom: 3 }}/>

            <Card sx={{ mt: 2, p: 3, borderRadius: 2, boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12)' }}>
                
                <Typography variant="h6" color="primary" sx={{ mb: 2, fontWeight: 600 }}>Servicios por Defecto</Typography>
                <Grid container spacing={3} mb={4}>
                    <Grid size={{sm: 6, md: 4, xs:12}}>
                        {renderLabelWithInfo('Servicio Chat', 'defaultChatServiceId')}
                        <Typography variant="body1">{getServiceName(item.defaultChatServiceId) || 'No configurado'}</Typography>
                    </Grid>
                    <Grid size={{sm: 6, md: 4, xs:12}}>
                        {renderLabelWithInfo('Servicio Imagen', 'defaultImageServiceId')}
                        <Typography variant="body1">{getServiceName(item.defaultImageServiceId) || 'No configurado'}</Typography>
                    </Grid>
                    <Grid size={{sm: 6, md: 4, xs:12}}>
                        {renderLabelWithInfo('Servicio Audio', 'defaultAudioServiceId')}
                        <Typography variant="body1">{getServiceName(item.defaultAudioServiceId) || 'No configurado'}</Typography>
                    </Grid>
                    <Grid size={{sm: 6, md: 4, xs:12}}>
                        {renderLabelWithInfo('Servicio Embedding', 'defaultEmbeddingServiceId')}
                        <Typography variant="body1">{getServiceName(item.defaultEmbeddingServiceId) || 'No configurado'}</Typography>
                    </Grid>
                    <Grid size={{sm: 6, md: 4, xs:12}}>
                        {renderLabelWithInfo('Servicio OCR', 'defaultOcrServiceId')}
                        <Typography variant="body1">{getServiceName(item.defaultOcrServiceId) || 'No configurado'}</Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 3 }} />

                <Typography variant="h6" color="primary" sx={{ mb: 2, fontWeight: 600 }}>Parámetros y Tokens</Typography>
                <Grid container spacing={3} mb={4}>
                    <Grid size={{ xs:12,sm:6, md:3 }}>
                        {renderLabelWithInfo('Créditos por USD', 'creditsPerUsd')}
                        <Typography variant="body1">{item.creditsPerUsd ?? 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs:12,sm:6, md:3 }}>
                        {renderLabelWithInfo('Temperatura', 'defaultTemperature')}
                        <Typography variant="body1">{item.defaultTemperature ?? 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs:12,sm:6, md:3 }}>
                        {renderLabelWithInfo('Top P', 'defaultTopP')}
                        <Typography variant="body1">{item.defaultTopP ?? 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs:12,sm:6, md:3 }}>
                        {renderLabelWithInfo('Tokens Máximos', 'defaultMaxTokens')}
                        <Typography variant="body1">{item.defaultMaxTokens ?? 'N/A'}</Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 3 }} />

                <Typography variant="h6" color="primary" sx={{ mb: 2, fontWeight: 600 }}>Opciones Avanzadas</Typography>
                <Grid container spacing={3}>
                    <Grid size={{ xs:12,sm:6, md:2 }}>
                        {renderLabelWithInfo('Streaming', 'enableStreaming', true)}
                        <Chip label={item.enableStreaming ? 'Habilitado' : 'Deshabilitado'} color={item.enableStreaming ? 'success' : 'default'} size="small" />
                    </Grid>
                    <Grid size={{ xs:12,sm:6, md:2 }}>
                        {renderLabelWithInfo('Memoria', 'enableMemory', true)}
                        <Chip label={item.enableMemory ? 'Habilitado' : 'Deshabilitado'} color={item.enableMemory ? 'success' : 'default'} size="small" />
                    </Grid>
                    <Grid size={{ xs:12,sm:6, md:2 }}>
                        {renderLabelWithInfo('Caché', 'enableCache', true)}
                        <Chip label={item.enableCache ? 'Habilitado' : 'Deshabilitado'} color={item.enableCache ? 'success' : 'default'} size="small" />
                    </Grid>
                    <Grid size={{ xs:12,sm:6, md:2 }}>
                        {renderLabelWithInfo('Tools', 'enableTools', true)}
                        <Chip label={item.enableTools ? 'Habilitado' : 'Deshabilitado'} color={item.enableTools ? 'success' : 'default'} size="small" />
                    </Grid>
                    <Grid size={{ xs:12,sm:6, md:2 }}>
                        {renderLabelWithInfo('Logs', 'enableLogs', true)}
                        <Chip label={item.enableLogs ? 'Habilitado' : 'Deshabilitado'} color={item.enableLogs ? 'success' : 'default'} size="small" />
                    </Grid>
                    <Grid size={{ xs:12,sm:6, md:2 }}>
                        {renderLabelWithInfo('BYOK', 'allowBringYourOwnKey', true)}
                        <Chip label={item.allowBringYourOwnKey ? 'Permitido' : 'No Permitido'} color={item.allowBringYourOwnKey ? 'success' : 'default'} size="small" />
                    </Grid>
                </Grid>

            </Card>
            
            <InfoModal
                open={activeModal !== null}
                setOpen={(val: boolean) => { if (!val) setActiveModal(null); }}
                title={modalContent.title}
                description={modalContent.description}
                infoText={modalContent.infoText}
                icon="Info"
            />
        </Box>
    );
};
